// Digest freshness thresholds. The 60-min `fresh→late` boundary is borrowed
// from FleetDashboardPage's LocationCard for consistency, but the 120-min
// `late→silent` boundary is INTENTIONALLY TIGHTER than the fleet's 240-min
// equivalent. Digest data quality degrades within minutes of a cycle-aware
// sweep, so we want reps to see a stronger signal sooner. See sc-5782
// investigation memory `digest-departing-zeros-bug.md` for the production
// data that motivated the tighter tolerance.
//
// Anywhere in tags-admin that surfaces "how old is this digest's data"
// should use this util (NOT raw FleetDashboardPage thresholds).
export const FRESH_THRESHOLD_MIN = 60;
export const SILENT_THRESHOLD_MIN = 120;

export type Freshness = 'fresh' | 'late' | 'silent';

export interface TimestampAge {
  /** Localized wall-clock representation (e.g. "10:42 AM"). */
  timeStr: string;
  /** Human "N minutes ago" / "N hours ago" string. */
  agoStr: string;
  /** Three-tier freshness bucket. */
  freshness: Freshness;
  /** Raw minute count for callers that need a finer signal than the bucket. */
  diffMinutes: number;
}

export function formatTimestampAge(timestamp: string): TimestampAge {
  const time = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - time.getTime();
  const diffMinutes = Math.max(0, Math.floor(diffMs / 60000));
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  const timeStr = time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  let agoStr: string;
  if (hours > 0) {
    agoStr = `${hours} hour${hours !== 1 ? 's' : ''}${
      minutes > 0 ? `, ${minutes} minute${minutes !== 1 ? 's' : ''}` : ''
    } ago`;
  } else {
    agoStr = `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  }

  const freshness: Freshness =
    diffMinutes >= SILENT_THRESHOLD_MIN
      ? 'silent'
      : diffMinutes >= FRESH_THRESHOLD_MIN
        ? 'late'
        : 'fresh';

  return { timeStr, agoStr, freshness, diffMinutes };
}

// MUI palette key per tier. `text.secondary` is used for fresh so the chip
// stays visually neutral when there's nothing to flag.
export function freshnessColor(freshness: Freshness): string {
  switch (freshness) {
    case 'silent':
      return 'error.main';
    case 'late':
      return 'warning.main';
    case 'fresh':
    default:
      return 'text.secondary';
  }
}
